import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import BackHeader from '../components/BackHeader';
import NavigationBar from '../components/NavigationBar';
import RestaurantItem from '../components/RestaurantItem';
import NoSearchResults from '../components/NoSearchResults';
import useTranslation from '../hooks/useTranslation';
import { useAddress } from '../contexts/AddressContext';
import { searchRestaurantsByMenu, searchRestaurantsByFranchise } from '../api/menuService';
import { Restaurant } from '../types/restaurant';

// 프랜차이즈 ID를 이름으로 매핑하는 객체
const franchiseIdToName: Record<string, string> = {
  'Zm7HtPk7': 'Matdakko',
  'Ut2NoLs3': 'BBQ',
  'Jk5WeHw4': 'BHC',
  'Lc5RcSy9': 'Kyochon Chicken',
  'Pq1UkOh4': 'Nene Chicken',
  'Xb1JhWt0': 'Goobne Chicken',
  'Nf2QrEc2': 'Gikoba Chicken'
};

const SearchResultPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  useTranslation();
  const { address } = useAddress();
  
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 중복 API 호출 방지를 위한 ref
  const isApiCallInProgress = useRef(false);
  
  const menuName = searchParams.get('menuName') || '';
  const categoryId = searchParams.get('categoryId') || '';
  const franchiseId = searchParams.get('franchiseId') || '';
  const searchTypeParam = searchParams.get('searchType') || 'MENU';
  const searchType = (searchTypeParam === 'FRANCHISE' ? 'FRANCHISE' : 'MENU') as 'MENU' | 'FRANCHISE';
  
  // 실제 검색어 결정 (franchiseId가 있으면 해당 프랜차이즈명 사용)
  const actualSearchTerm = franchiseId ? (franchiseIdToName[franchiseId] || franchiseId) : menuName;
  
  useEffect(() => {
    const fetchSearchResults = async () => {
      // 중복 API 호출 방지 - 이미 진행 중이면 리턴
      if (isApiCallInProgress.current) {
        return;
      }
      
      // 검색어도 없고 franchiseId도 없으면 에러
      if (!actualSearchTerm) {
        setError('Search term is required');
        setLoading(false);
        return;
      }
      
      // address 객체의 필수 필드가 모두 존재하는지 엄격하게 확인
      if (!address || 
          typeof address.lat !== 'number' || 
          typeof address.lng !== 'number' ||
          !address.addressId) {
        setError('Location is required for search');
        setLoading(false);
        return;
      }
      
      // API 호출 시작 플래그 설정
      isApiCallInProgress.current = true;
      
      try {
        setLoading(true);
        setError(null);
        
        let results: Restaurant[];
        
        if (franchiseId) {
          // 프랜차이즈 ID로 검색
          results = await searchRestaurantsByFranchise(
            address.lat,
            address.lng,
            franchiseId,
            10, // 더 많은 결과를 가져오도록 설정
            address.addressId // addressId 추가
          );
        } else {
          // 메뉴명으로 검색
          results = await searchRestaurantsByMenu(
            address.lat,
            address.lng,
            menuName,
            categoryId || undefined,
            address.addressId // addressId 추가
          );
        }
        
        setRestaurants(results);
      } catch (err) {
        setError('Failed to load search results. Please try again.');
      } finally {
        setLoading(false);
        // API 호출 완료 플래그 리셋
        isApiCallInProgress.current = false;
      }
    };
    
    fetchSearchResults();
  }, [actualSearchTerm, franchiseId, categoryId, menuName, address]);

  const handleRetry = () => {
    if (actualSearchTerm && address?.lat && address?.lng) {
      // 재시도시에도 중복 호출 방지
      if (isApiCallInProgress.current) {
        return;
      }
      
      setLoading(true);
      setError(null);
      isApiCallInProgress.current = true;
      
      // useEffect가 다시 실행되도록 트리거
      const fetchResults = async () => {
        try {
          let results: Restaurant[];
          
          if (franchiseId) {
            results = await searchRestaurantsByFranchise(
              address.lat!,
              address.lng!,
              franchiseId,
              10,
              address.addressId // addressId 추가
            );
          } else {
            results = await searchRestaurantsByMenu(
              address.lat!,
              address.lng!,
              menuName,
              categoryId || undefined,
              address.addressId // addressId 추가
            );
          }
          
          setRestaurants(results);
        } catch (err) {
          setError('Failed to load search results. Please try again.');
        } finally {
          setLoading(false);
          isApiCallInProgress.current = false;
        }
      };
      fetchResults();
    }
  };
  
  if (loading) {
    return (
      <>
        <BackHeader title="Search Results" />
        <div className="p-4 pb-20">
          <div className="text-center py-8">
            <div className="text-gray-500">Loading search results...</div>
          </div>
        </div>
        <NavigationBar />
      </>
    );
  }
  
  if (error) {
    return (
      <>
        <BackHeader title="Search Results" />
        <div className="p-4 pb-20">
          <div className="text-center py-8">
            <div className="text-red-500">{error}</div>
          </div>
        </div>
        <NavigationBar />
      </>
    );
  }
  
  return (
    <>
      <BackHeader title="Search Results" />
      <div className="p-4 pb-20">

        
        {/* 검색 결과 리스트 */}
        {restaurants.length === 0 ? (
          <NoSearchResults 
            searchTerm={actualSearchTerm}
            searchType={searchType}
            onRetry={handleRetry}
          />
        ) : (
          <div className="space-y-4">
            {restaurants.map((restaurant) => (
              <RestaurantItem
                key={restaurant.id}
                restaurant={restaurant}
              />
            ))}
          </div>
        )}
      </div>
      <NavigationBar />
    </>
  );
};

export default SearchResultPage;
